/* ============================================================
   SENARAI AGENT — bacaan RINGKAS untuk hub PRO Studio
   ============================================================
   Hub hanya perlu memapar baris: nama, status, dan tiga angka
   ringkasan. Ia TIDAK mengedit agent, jadi ia tidak memerlukan
   pemetaan penuh `fromDoc` dalam ProStudioAgentSection (medan,
   sumber padanan, balasan berturutan dan sebagainya).

   ⚠️ Kalau hub suatu hari nanti perlu MENGEDIT agent, jangan
      luaskan hook ini — panggil seksyen sebenar. Pemetaan penuh
      hidup di satu tempat sahaja, dan ia mesti kekal begitu.

   Penapis `agentV === 2` adalah WAJIB: collection yang sama turut
   memegang template Smart Custom lama, dan tanpa penapis itu
   template lama muncul dalam senarai sebagai agent.
   ============================================================ */

import { useEffect, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';

import { db, auth } from '../firebase';
/* Susunan pelanggan sendiri — satu pembanding, dikongsi dengan senarai
   penuh dalam halaman Agent dan pemilih agent di Workspace. */
import { agentDiWorkspace, bandingAgent } from '../utils/susunAgent';
/* Bilangan pusingan "Simpan gambar" — SATU takrifan dengan larian sebenar,
   semakan ruang gambar dan pemilih agent di Workspace. */
import { imageTurnsDaripadaAgent } from '../utils/agentGambar';

const AGENT_COL = 'chatgptTemplates';
const AGENT_V = 2;

/* `hidup` — beri `false` untuk langsung tidak membaca (contoh: pelanggan
   tiada akses SMART, atau halaman dalam mod pelan). Bacaan yang tidak
   dipapar tetap dikira kos. */
export function useSenaraiAgent(hidup = true) {
    /* Satu objek, bukan dua state berasingan — supaya keadaan dikemas kini
       sekali sahaja bagi setiap snapshot.

       ⚠️ TIADA setState dalam badan effect. Keadaan "belum muat" dan
          "dimatikan" DIKIRA pada masa pulang di bawah; menetapkannya dalam
          effect mencetuskan render bertingkat (dan lint melarangnya). */
    const [keadaan, setKeadaan] = useState({ agents: [], sudahMuat: false });

    useEffect(() => {
        const uid = auth.currentUser?.uid;
        if (!hidup || !uid) return undefined;

        const unsub = onSnapshot(
            collection(db, 'users', uid, AGENT_COL),
            (snap) => {
                const senarai = snap.docs
                    .map((d) => ({ id: d.id, d: d.data() || {} }))
                    .filter((x) => Number(x.d.agentV) === AGENT_V)
                    /* Disusun pada doc MENTAH, sebelum pemetaan — itu yang
                       membenarkan satu pembanding berkhidmat kepada ketiga-tiga
                       senarai agent, yang bentuk UI-nya berbeza-beza. */
                    .sort((a, b) => bandingAgent(a.d, b.d))
                    .map((x) => ({
                        id: x.id,
                        configured: !!x.d.configured,
                        /* Toggle "Papar di Workspace" pada baris. Tiada medan = papar. */
                        diWorkspace: agentDiWorkspace(x.d),
                        /* Bentuk ini padan dengan SenaraiAgentV39 supaya baris
                           hub dan baris halaman senarai betul-betul sama. */
                        data: {
                            nama: String(x.d.name || 'Agent tanpa nama'),
                            set: Number(x.d.setCount) || 1,
                            babak: Number(x.d.sceneCount) || 1,
                            hasil: x.d.output === 'image' ? 'gambar' : 'gambarvideo',
                            platform: x.d.platform === 'gemini' ? 'gemini' : 'chatgpt',
                            /* Dikira daripada doc MENTAH, sama seperti WorkspaceView —
                               doc tersimpan memang membawa `firstImage` + `replyImages`
                               sejajar dengan `replies`. 0 = agent tidak menyimpan gambar. */
                            gambar: imageTurnsDaripadaAgent(x.d).length,
                        },
                    }));
                setKeadaan({ agents: senarai, sudahMuat: true });
            },
            // Senyap: hub bukan tempat melaporkan ralat. Halaman Agent yang
            // memaparkan mesej ralat sebenar bila bacaan gagal.
            () => setKeadaan({ agents: [], sudahMuat: true }),
        );
        return () => unsub();
    }, [hidup]);

    return {
        agents: hidup ? keadaan.agents : [],
        memuat: hidup && !keadaan.sudahMuat,
    };
}

export default useSenaraiAgent;
